import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api } from "../api/client";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import { Button, Card, Empty, Field, Input, Loader, Modal, Pagination } from "../components/ui";

const emptyForm = { name: "", identity: "", email: "", password: "", isActive: true };

export default function Builders() {
  const { isAdmin, setBuilderScope } = useAuth();
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1 });
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api("/builders", { query: { search, page, limit: 12 } });
      setItems(res.data?.items || []);
      setPagination(res.data?.pagination || { page: 1, totalPages: 1 });
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  if (!isAdmin) return <Navigate to="/" replace />;

  const startCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setOpen(true);
  };

  const startEdit = (builder) => {
    setEditing(builder);
    setForm({
      name: builder.name,
      identity: builder.identity,
      email: builder.email,
      password: "",
      isActive: builder.isActive,
    });
    setOpen(true);
  };

  const save = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      if (editing) {
        const body = {
          name: form.name,
          identity: form.identity,
          email: form.email,
          isActive: form.isActive,
        };
        if (form.password) body.password = form.password;
        await api(`/builders/${editing.id}`, { method: "PATCH", body });
        toast.success("Builder updated");
      } else {
        await api("/builders", {
          method: "POST",
          body: {
            name: form.name,
            identity: form.identity,
            email: form.email,
            password: form.password,
          },
        });
        toast.success("New builder added");
      }
      setOpen(false);
      load();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (builder) => {
    if (!window.confirm(`Delete ${builder.name}?`)) return;
    try {
      await api(`/builders/${builder.id}`, { method: "DELETE" });
      toast.success("Builder deleted");
      load();
    } catch (error) {
      toast.error(error.message);
    }
  };

  return (
    <div className="stack">
      <header className="page-head">
        <div>
          <p className="eyebrow">Admin</p>
          <h1>Builders</h1>
        </div>
        <Button icon="plus" onClick={startCreate}>
          New builder
        </Button>
      </header>

      <form
        className="filters"
        onSubmit={(event) => {
          event.preventDefault();
          setPage(1);
          load();
        }}
      >
        <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Name, email or code" />
        <Button type="submit" variant="ghost">
          Search
        </Button>
      </form>

      {loading ? (
        <Loader />
      ) : items.length === 0 ? (
        <Empty title="No builders yet" text="Add a builder, then give them a login." />
      ) : (
        <div className="card-grid">
          {items.map((builder) => (
            <Card key={builder.id}>
              <div className="sale-card-top">
                <h3>{builder.name}</h3>
                <span className={`pill ${builder.isActive ? "pill-open" : "pill-closed"}`}>
                  {builder.isActive ? "Active" : "Stopped"}
                </span>
              </div>
              <p className="muted">{builder.email}</p>
              <p>
                Code: <b className="num">{builder.identity}</b>
              </p>
              <div className="row-actions">
                <Button variant="ghost" onClick={() => setBuilderScope(builder.id)}>
                  View books
                </Button>
                <Button variant="ghost" onClick={() => startEdit(builder)}>
                  Edit
                </Button>
                <Button variant="danger" onClick={() => remove(builder)}>
                  Delete
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Pagination page={pagination.page} totalPages={pagination.totalPages} onPage={setPage} />

      <Modal
        open={open}
        title={editing ? "Edit builder" : "New builder"}
        onClose={() => setOpen(false)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" form="builder-form" disabled={busy}>
              Save
            </Button>
          </>
        }
      >
        <form id="builder-form" className="form-grid" onSubmit={save}>
          <Field label="Name">
            <Input
              required
              minLength={2}
              value={form.name}
              onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
            />
          </Field>
          <Field label="ID code" hint="Example: BLD-01">
            <Input
              required
              minLength={3}
              value={form.identity}
              onChange={(event) => setForm((prev) => ({ ...prev, identity: event.target.value.toUpperCase() }))}
            />
          </Field>
          <Field label="Email">
            <Input
              type="email"
              required
              value={form.email}
              onChange={(event) => setForm((prev) => ({ ...prev, email: event.target.value }))}
            />
          </Field>
          <Field label={editing ? "New password (optional)" : "Password"} hint="At least 8 characters">
            <Input
              type="password"
              minLength={editing ? undefined : 8}
              required={!editing}
              value={form.password}
              onChange={(event) => setForm((prev) => ({ ...prev, password: event.target.value }))}
            />
          </Field>
          {editing ? (
            <label className="check field-wide">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(event) => setForm((prev) => ({ ...prev, isActive: event.target.checked }))}
              />
              Keep this account active
            </label>
          ) : null}
        </form>
      </Modal>
    </div>
  );
}
